import * as Sentry from "@sentry/react"
import type { ReactElement, ReactNode } from "react"

export interface AppErrorBoundaryFallbackProps {
  /** The thrown value. Untyped — render-time throws are not always Errors. */
  error: unknown
  /** Re-mounts the wrapped subtree to retry after a transient failure. */
  reset: () => void
}

export interface AppErrorBoundaryProps {
  children: ReactNode
  /**
   * App-specific fallback UI. When omitted, a neutral default is rendered.
   * Apps pass this to match their design system (consumer vs pro chrome).
   * Returns a ReactElement (Sentry's FallbackRender contract) — JSX satisfies it.
   */
  fallback?: (props: AppErrorBoundaryFallbackProps) => ReactElement
  /**
   * Extra Sentry tags applied to the captured event via Sentry's
   * `beforeCapture` hook (e.g. `{ posture: "marketplace", rail: "self_report" }`).
   * Callers own the anonymity discipline for whatever they pass here — this
   * component does not inspect values — so only enum/string dimensions
   * belong in this map, never a financial amount or derived figure (ADR-030
   * D13; see the M3 funnel boundary at `components/funnel/funnel-sentry-tags.ts`
   * for a worked allowlist example). Omitted for boundaries that don't need
   * extra dimensioning.
   */
  tags?: Record<string, string>
}

/**
 * AppErrorBoundary wraps a routed React subtree so a render-time throw is
 * reported to Sentry AND shows a recoverable fallback instead of unmounting the
 * whole tree to a blank screen.
 *
 * Why this exists (ADR-030 P2): `reactRouterV7BrowserTracingIntegration`
 * instruments NAVIGATION transactions only — it does not catch render errors. A
 * component that throws during render produced zero Sentry events and a white
 * screen. `Sentry.ErrorBoundary` captures the exception with the component stack
 * and a React `errorBoundary` mechanism tag.
 *
 * The predicate-based `captureHandledError` covers the API boundary; this covers
 * the render boundary. Together they close the two FE capture gaps.
 */
export function AppErrorBoundary({ children, fallback, tags }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        if (!tags) return
        for (const [key, value] of Object.entries(tags)) {
          scope.setTag(key, value)
        }
      }}
      fallback={({ error, resetError }) =>
        fallback
          ? fallback({ error, reset: resetError })
          : defaultFallback(resetError)
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

function defaultFallback(reset: () => void): ReactElement {
  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-1 flex-col items-center justify-center gap-3 p-8 text-center"
    >
      <p className="text-lg font-semibold">Something went wrong</p>
      <p className="max-w-sm text-sm opacity-70">
        An unexpected error occurred. The team has been notified. You can retry,
        or reload the page if the problem persists.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={reset}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Reload
        </button>
      </div>
    </div>
  )
}
